import React from 'react';
import {
  IonButton, IonButtons, IonCheckbox, IonContent, IonFooter, IonHeader, IonInput, IonLabel,
  IonModal, IonTextarea, IonTitle, IonToolbar,
} from '@ionic/react';
import { CalendarLog } from '../../../datamodels';

interface LogModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  dateStr: string;
  mood?: 'good' | 'neutral' | 'bad';
  setMood: (mood: 'good' | 'neutral' | 'bad') => void;
  note: string;
  setNote: (note: string) => void;
  onPeriod: boolean;
  setOnPeriod: (value: boolean) => void;
  workoutTitle: string;
  setWorkoutTitle: (value: string) => void;
  workoutTime: string;
  setWorkoutTime: (value: string) => void;
  saveLog: () => Promise<void>;
  downloadCalendarEvent: () => void;
  openGoogleCalendar: () => void;
  existingLog?: CalendarLog;
  saveError: string;
}

const LogModal: React.FC<LogModalProps> = props => {
  const canSave = Boolean(props.mood || props.note.trim() || props.onPeriod || props.workoutTitle.trim() || props.existingLog);
  const hasWorkoutPlan = Boolean(props.workoutTitle.trim());

  return (
  <IonModal isOpen={props.isOpen} onDidDismiss={props.onDismiss} initialBreakpoint={0.92} breakpoints={[0, 0.92, 1]} className="calendar-log-modal">
    <IonHeader><IonToolbar><IonTitle>{new Date(`${props.dateStr}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</IonTitle><IonButtons slot="end"><IonButton strong onClick={() => void props.saveLog()} disabled={!canSave}>Save</IonButton></IonButtons></IonToolbar></IonHeader>
    <IonContent className="calendar-modal-content">
      <section className="calendar-form-section">
        <span className="calendar-form-label">How did the day feel?</span>
        <div className="mood-options">
          <button className={props.mood === 'good' ? 'selected' : ''} onClick={() => props.setMood('good')}><b>😊</b><span>Good</span></button>
          <button className={props.mood === 'neutral' ? 'selected' : ''} onClick={() => props.setMood('neutral')}><b>😐</b><span>Neutral</span></button>
          <button className={props.mood === 'bad' ? 'selected' : ''} onClick={() => props.setMood('bad')}><b>☹️</b><span>Low</span></button>
        </div>
      </section>

      <section className="calendar-form-section calendar-toggle-row">
        <div><strong>Period day</strong><small>Add this day to the local cycle overview.</small></div>
        <IonCheckbox checked={props.onPeriod} onIonChange={event => props.setOnPeriod(event.detail.checked)} />
      </section>

      <section className="calendar-form-section workout-plan-fields">
        <div className="workout-plan-heading"><strong>Workout plan</strong><small>Optional · leave the name empty if you only want to log the day.</small></div>
        <IonLabel>Workout name</IonLabel><IonInput value={props.workoutTitle} placeholder="e.g. Lower body" onIonInput={event => props.setWorkoutTitle(event.detail.value || '')} />
        <IonLabel>Time</IonLabel><IonInput type="time" value={props.workoutTime} onIonInput={event => props.setWorkoutTime(event.detail.value || '')} />
        <div className="external-calendar-actions">
          <IonButton shape="round" fill="outline" disabled={!hasWorkoutPlan} onClick={props.downloadCalendarEvent}>Download .ics</IonButton>
          <IonButton shape="round" fill="outline" disabled={!hasWorkoutPlan} onClick={props.openGoogleCalendar}>Open in Google Calendar</IonButton>
        </div>
        <p>Nothing is synced automatically. Only the workout name, date and time are copied. Your mood, cycle data and private note stay local.</p>
      </section>

      <section className="calendar-form-section"><IonLabel>Private note · optional</IonLabel><IonTextarea value={props.note} placeholder="Energy, symptoms, a reminder…" autoGrow rows={3} onIonInput={event => props.setNote(event.detail.value || '')} /></section>
      {props.saveError && <p className="calendar-save-error" role="alert">{props.saveError}</p>}
    </IonContent>
    <IonFooter><IonToolbar><div className="calendar-modal-footer"><IonButton shape="round" fill="clear" color="medium" onClick={props.onDismiss}>Cancel</IonButton><IonButton shape="round" onClick={() => void props.saveLog()} disabled={!canSave}>Save day</IonButton></div></IonToolbar></IonFooter>
  </IonModal>
  );
};

export default LogModal;
